import React, { useRef } from 'react';
import { Project } from '../data/projects';

interface ProjectCardProps {
  project: Project;
  onClick: (id: string) => void;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, onClick }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const isVideo = project.image.toLowerCase().endsWith('.mp4');
  const hasModel = Boolean(project.stlUrl);

  const handleOpen = () => onClick(project.id);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpen();
    }
  };

  const handleActionClick = (event: React.MouseEvent) => {
    // Keep link clicks from also opening the modal.
    event.stopPropagation();
  };

  const handleMouseEnter = () => {
    const video = videoRef.current;
    if (video) {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => undefined);
      }
    }
  };

  const handleMouseLeave = () => {
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  };

  return (
    <article
      className="card"
      data-category={project.category}
      role="button"
      tabIndex={0}
      aria-label={`Open details for ${project.title}`}
      onClick={handleOpen}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="card__media">
        {isVideo ? (
          <video
            ref={videoRef}
            src={project.image}
            preload="metadata"
            muted
            loop
            playsInline
            className="card__visual"
          />
        ) : (
          <img
            src={project.image}
            alt={project.title}
            className="card__visual"
            loading="lazy"
          />
        )}

        <div className="card__scrim" aria-hidden="true" />

        <div className="card__tags">
          <span className="card__tag">{project.categoryLabel}</span>
          {hasModel && <span className="card__tag card__tag--model">3D View</span>}
        </div>

        <div className="card__actions">
          <a
            href={project.githubUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="card__action"
            aria-label={`${project.title} source`}
            onClick={handleActionClick}
          >
            <i className="ri-github-line"></i>
          </a>

          {project.demoUrl && (
            <a
              href={project.demoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="card__action"
              aria-label={`${project.title} live demo`}
              onClick={handleActionClick}
            >
              <i className="ri-external-link-line"></i>
            </a>
          )}
        </div>
      </div>

      <div className="card__body">
        <h3 className="card__title">{project.title}</h3>
        <p className="card__summary">{project.description}</p>
        <ul className="card__tech">
          {project.technologies.slice(0, 4).map(tech => (
            <li className="card__tech-item" key={tech}>{tech}</li>
          ))}
        </ul>
      </div>
    </article>
  );
};

export default ProjectCard;